import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	Stack,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useEffect, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import MeditationInfoForm from './components/AchievementInfoForm';
import { AwardType, OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { GridColDef } from '@mui/x-data-grid';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import SetSingleStatusDialog from './components/SetSingleStatusDialog';
import { useDeleteBodyMutation } from '@/api/exercise/bodyApiSlice';
import {
	useDeleteAchievementMutation,
	useGetAchievementDetailQuery,
	useGetAchievementSingleListQuery,
} from '@/api/task/achievementApiSlice';
import {
	AchievementFilter,
	TaskAchievementRewardDetailDTO,
} from '@/api/DTO/task/achievement.interface';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import PublishedChip from '@/common/components/PublishedChip';
import WarningIcon from '@mui/icons-material/Warning';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function AchievementDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const { achievementId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [deleteAchievement] = useDeleteAchievementMutation();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const { data: details } = useGetAchievementDetailQuery(achievementId || '', {
		refetchOnMountOrArgChange: true,
	});
	const defaultPropertyFilter: AchievementFilter = {
		pagination: initialPagination,
		sortings: [],
		id: achievementId || '',
	};

	const defaultValues: DefaultValues<AchievementFilter> = defaultPropertyFilter;
	const methods = useForm<AchievementFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as AchievementFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: AchievementFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'published',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetAchievementSingleListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: TaskAchievementRewardDetailDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	); // watch fields change

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.published == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('achievementList.delete')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('achievementList.isDelete')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAchievement(achievementId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/task-manage/achievement-list', { replace: true });
					}
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'sort',
			headerName: t('achievementList.level'),
			flex: 25,
			renderCell: (params: any) => params.row.sort || '-',
		},
		{
			field: 'finishedNum',
			headerName: t('achievementList.finishTotal'),
			flex: 25,
		},
		{
			field: 'rewardType',
			headerName: t('achievementList.rewardType'),
			flex: 25,
			renderCell: (params: any) =>
				showLabel(params.row.rewardType, awardTypeList),
		},
		{
			field: 'rewardPoints',
			headerName: t('achievementList.rewardPoints'),
			flex: 25,
			renderCell: (params: any) => params.row.rewardPoints || '-',
		},
		{
			field: 'rewardDesc',
			headerName: t('achievementList.rewardProp'),
			flex: 25,
			renderCell: (params: any) => params.row.rewardDesc || '-',
		},
		{
			field: 'published',
			headerName: t('achievementList.taskStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<>
					<PublishedChip
						published={params.row.published}
						publishList={PUBLISHED_LISTB}
					/>
					{params.row.published == OnoffType.on &&
						params.row.allRewardUnpublished && (
							<Tooltip
								title={`${t('regularTask.statusTips')}`}
								sx={{ color: '#FA9E26' }}
							>
								<WarningIcon />
							</Tooltip>
						)}
				</>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('achievementList.updateTime'),
			flex: 20,
		},
		{
			field: 'createdAt',
			headerName: t('achievementList.createTime'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/task-manage/achievement-list/info/${achievementId}/detail/${params.row.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.achievementDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('achievementList.seriesStatus')}
									valueElement={
										<PublishedChip
											published={details?.data.published}
											publishList={PUBLISHED_LISTB}
										/>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('achievementList.seriesName')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<AppFieldView
													label={t('achievementList.completionConditions')}
													value={details?.data.finishedConditionName}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.pushedTime')}
													value={
														!details?.data.publishedStartAt
															? '-'
															: dayjs(details?.data.publishedStartAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('taskManage.pulledTime')}
													value={
														!details?.data.publishedEndAt
															? '-'
															: dayjs(details?.data.publishedEndAt)
																	.locale('en')
																	.format(DISPLAY_DATETIME_12H_FORMAT)
													}
												/>
											</Grid>
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<MeditationInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>

				<Card sx={{ marginTop: 3 }}>
					<CardContent>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h6'>{t('taskManage.taskList')}</Typography>
							<Stack direction={'row'} alignItems={'center'} spacing={3}>
								<SetSingleStatusDialog />
								<Button
									variant='contained'
									style={{ alignItems: 'center' }}
									endIcon={<NoteAltOutlinedIcon />}
									onClick={() =>
										navigate(
											`/task-manage/achievement-list/info/${achievementId}/add`
										)
									}
								>
									{t('button.add')}
								</Button>
							</Stack>
						</Box>
						<FormProvider {...methods}>
							<Box marginTop={2}>
								<AppDataGrid
									loading={!isGetPropertySummarySuccess}
									rows={transformResponse(propertySummary?.data?.items) ?? []}
									rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.id}
								/>
							</Box>
						</FormProvider>
					</CardContent>
				</Card>
			</Box>
		</>
	);
}
